import React from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Tag,
  Text,
  VStack,
} from "@chakra-ui/react";
import { SelectedDeliveryOption } from "../../types";
import { ValidationError } from "../../validation/deliverySelectionValidation";
import { QuickEtaOption } from "../../hooks/useManualOptionHandlers";
import { CPTSlabForManualOption } from "../../../../views/Orders/enums";
import { CPTSlabSelector } from "./CPTSlabSelector";
import {
  DELETE_COLUMN_WIDTH,
  OPTION_ROW_OUTER_GAP,
  getDeliveryOptionCardStyles,
} from "../constants/deliveryOptionRowLayout";

export interface ManualDeliveryOptionFormProps {
  isManualSelected: boolean;
  isExpired: boolean;
  isRemainingItems: boolean;
  selection: SelectedDeliveryOption | undefined;
  quickEtaOptions: QuickEtaOption[];
  errorsByField: Record<string, ValidationError[] | undefined>;
  manualSaveError: string | null;
  isSavingManualOption: boolean;
  onManualOptionSelect: () => void;
  onManualCPTSlabChange: (val: CPTSlabForManualOption) => void;
  onManualCPTChange: (value: string) => void;
  onManualChargeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onQuickEtaSelect: (minutes: number) => void;
  onCancel: () => void;
  onSave: () => void;
}

export const ManualDeliveryOptionForm: React.FC<
  ManualDeliveryOptionFormProps
> = ({
  isManualSelected,
  isExpired,
  isRemainingItems,
  selection,
  quickEtaOptions,
  errorsByField,
  manualSaveError,
  isSavingManualOption,
  onManualOptionSelect,
  onManualCPTSlabChange,
  onManualCPTChange,
  onManualChargeChange,
  onQuickEtaSelect,
  onCancel,
  onSave,
}) => {
  const isInteractionDisabled = isExpired || isRemainingItems;
  const cardStyles = getDeliveryOptionCardStyles({
    isSelected: isManualSelected,
    isInteractionDisabled,
  });

  return (
    <HStack spacing={OPTION_ROW_OUTER_GAP} align="center" w="100%">
      <Box
        flex={1}
        {...cardStyles}
        onClick={() => !isInteractionDisabled && onManualOptionSelect()}
      >
        <Flex justify="space-between" align="center" w="100%">
          <HStack spacing={3} align="center">
            <Text
              fontSize="0.75rem"
              fontWeight="700"
              textTransform="uppercase"
              color="purple.600"
              fontFamily="'Nunito','Inter', sans-serif"
              letterSpacing="wider"
            >
              + ADD OPTION
            </Text>
          </HStack>

          {isManualSelected && (
            <CPTSlabSelector
              value={selection?.deliveryDetails?.manualEntry?.cptSlab}
              onChange={onManualCPTSlabChange}
              isDisabled={isExpired}
            />
          )}
        </Flex>

        {isManualSelected && (
          <VStack
            align="stretch"
            spacing={3}
            mt={2}
            onClick={(e) => e.stopPropagation()}
          >
            <Box>
              <Text
                fontSize="0.7rem"
                color="gray.500"
                fontWeight="700"
                mb={2}
              >
                QUICK SELECT CPT
              </Text>
              <Flex wrap="wrap" gap={2}>
                {quickEtaOptions.map((opt) => (
                  <Tag
                    key={opt.label}
                    size="sm"
                    variant="subtle"
                    colorScheme="blue"
                    cursor={isExpired ? "not-allowed" : "pointer"}
                    onClick={() => onQuickEtaSelect(opt.minutes)}
                    opacity={isExpired ? 0.6 : 1}
                    _hover={{
                      bg: isExpired ? undefined : "blue.100",
                    }}
                    fontFamily="'Nunito','Inter', sans-serif"
                  >
                    {opt.label}
                  </Tag>
                ))}
              </Flex>
            </Box>

            <HStack spacing={4} align="start">
              <Input
                type="datetime-local"
                size="sm"
                fontSize="0.85rem"
                fontWeight="500"
                placeholder="Select CPT"
                borderColor={
                  selection?.deliveryDetails?.manualEntry?.cpt
                    ? "gray.300"
                    : "red.300"
                }
                _focus={{
                  borderColor: "blue.400",
                  boxShadow: "0 0 0 1px var(--chakra-colors-blue-400)",
                }}
                value={selection?.deliveryDetails?.manualEntry?.cpt || ""}
                onChange={(e) => onManualCPTChange(e.target.value)}
                isDisabled={isExpired || isRemainingItems}
                flex="1"
              />
              <InputGroup w="140px" size="sm">
                <InputLeftElement
                  pointerEvents="none"
                  fontSize="0.8rem"
                  color="gray.500"
                >
                  ₹
                </InputLeftElement>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="0"
                  fontSize="0.85rem"
                  fontWeight="600"
                  textAlign="right"
                  value={
                    selection?.deliveryDetails?.manualEntry?.deliveryCharge ??
                    ""
                  }
                  onChange={onManualChargeChange}
                  isDisabled={
                    isExpired ||
                    isRemainingItems ||
                    selection?.deliveryDetails?.manualEntry?.cptSlab ===
                      CPTSlabForManualOption.SELF_PICKUP
                  }
                  borderColor={
                    selection?.deliveryDetails?.manualEntry?.deliveryCharge !==
                    undefined
                      ? "gray.300"
                      : "red.300"
                  }
                  _focus={{
                    borderColor: "blue.400",
                    boxShadow: "0 0 0 1px var(--chakra-colors-blue-400)",
                  }}
                />
              </InputGroup>
            </HStack>
            {errorsByField.deliveryCharge?.map((error: ValidationError) => (
              <Text
                key={error.id}
                fontSize="12px"
                fontWeight="semibold"
                color="red.500"
                mt={1}
                ml={1}
              >
                *{error.message}
              </Text>
            ))}

            <HStack justify="flex-end" spacing={2} pt={1}>
              <Button
                size="sm"
                variant="outline"
                borderRadius="full"
                onClick={onCancel}
                isDisabled={isExpired || isRemainingItems}
                fontFamily="'Nunito','Inter', sans-serif"
                fontWeight="700"
              >
                CANCEL
              </Button>
              <Button
                size="sm"
                colorScheme="blue"
                borderRadius="full"
                onClick={onSave}
                isLoading={isSavingManualOption}
                isDisabled={
                  isExpired ||
                  isRemainingItems ||
                  !selection?.deliveryDetails?.manualEntry?.cpt ||
                  !selection?.deliveryDetails?.manualEntry?.cptSlab
                }
                fontFamily="'Nunito','Inter', sans-serif"
                fontWeight="800"
              >
                SAVE
              </Button>
            </HStack>
            {manualSaveError && (
              <Text
                fontSize="12px"
                fontWeight="semibold"
                color="red.500"
                mt={1}
                ml={1}
              >
                *{manualSaveError}
              </Text>
            )}
          </VStack>
        )}
      </Box>
      {!isRemainingItems && <Box w={DELETE_COLUMN_WIDTH} flexShrink={0} />}
    </HStack>
  );
};
